import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IPart, defaultValue } from 'app/shared/model/part.model';

const initialState: EntityState<IPart> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/parts';

// Actions

interface IPagedPartQueryParams extends IQueryParams {
  nameSearch?: string;
}

export const getEntities = createAsyncThunk('part/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IPart[]>(requestUrl);
});

export const getPartsByCompany = createAsyncThunk('maintenancePart/fetch_entity_list_by_company', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}/by-company${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IPart[]>(requestUrl);
});

export const getPartsByReview = createAsyncThunk('maintenancePart/fetch_entity_list_by_maintenance-review', async (maintenanceReviewId:number) => {
  const requestUrl = `${apiUrl}/by-maintenance-review/${maintenanceReviewId}`;
  const response = await axios.get<IPart[]>(requestUrl);
  return response.data;
});


export const getPartDataSummary = createAsyncThunk('part/fetch_entity_list', async ({ page, size, sort, nameSearch }: IPagedPartQueryParams) => {
  const requestUrl = `${apiUrl}?${sort ? `page=${page}&size=${size}&sort=${sort}&nameSearch=${nameSearch}&` : ''}cacheBuster=${new Date().getTime()}`;
  return axios.get<IPart[]>(requestUrl);
});

export const getEntity = createAsyncThunk(
  'part/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IPart>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'part/create_entity',
  async (entity: IPart, thunkAPI) => {
    const result = await axios.post<IPart>(apiUrl, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'part/update_entity',
  async (entity: IPart, thunkAPI) => {
    const result = await axios.put<IPart>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'part/partial_update_entity',
  async (entity: IPart, thunkAPI) => {
    const result = await axios.patch<IPart>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'part/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IPart>(requestUrl);
    // thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const PartSlice = createEntitySlice({
  name: 'part',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getPartsByCompany), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getPartsByReview), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload,
          totalItems: action.payload.length,
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getPartsByCompany, getPartsByReview), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = PartSlice.actions;

// Reducer
export default PartSlice.reducer;
